/**
 * Rack list row — web: table columns + status bar; native: card + preview chips (#995 / #1052)
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  Pressable,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Modal,
  Dimensions,
} from 'react-native';

import {
  formatRackTableCell,
  getRackColumnSubline,
  getRackDisplayName,
  rackColumnUsesDoubleLine,
  rackTableColumnsForLayout,
  rackTableHeaderLabel,
  RACK_TABLE_COLUMN_ALIGN,
  type RackTableColumnKey,
  type RackTableLayout,
} from './rackListUtils';
import {
  RecordTableHeader,
  RECORD_TABLE_ACTIONS_COLUMN_WIDTH,
  recordTableStyles,
  useTableRowDragAndDrop,
} from '../recordTable';
import { rackColumnCellStyle } from './rackTableColumnLayout';
import {
  RACK_ROW_ACTION_DELETE_LABEL,
  RACK_ROW_ACTION_DUPLICATE_LABEL,
  RACK_ROW_ACTION_EDIT_LABEL,
  RACK_ROW_ACTION_VIEW_LABEL,
} from '../../constants/rackConfigurationsSubsectionCopy';
import { colors, spacing, layout } from '../../theme';
import { isNativeCompactLayout } from '../../utils/locationTagListDisplay';
import { MoreHorizontal, Copy, Trash2, GripVertical, Edit } from '../icons';
import { LocationRoomCell } from '../locationTags/LocationRoomCell';
import { NativeCompactNameCell } from '../recordTable/NativeCompactNameCell';
import { RecordTableCellSubline } from '../recordTable/RecordTableCellSubline';
import {
  repeatableCompactActionsColumnReserved,
  repeatableRowDragState,
  repeatableRowMenuState,
} from '../recordTable/repeatableRowChrome';
import { RepeatableUnitStatusPill } from '../recordTable/RepeatableUnitStatusPill';

import type { RackConfiguration } from '../../types/rack';

const RACK_ROW_MENU_MIN_WIDTH = 180;
/** Approx height of one menu row; used to flip the native dropdown up near the screen bottom. */
const RACK_ROW_MENU_ITEM_HEIGHT = 44;
const RACK_ROW_MENU_PORTAL_ATTR = 'data-rack-row-menu-portal';
const RACK_ROW_MENU_BUTTON_ATTR = 'data-rack-row-menu-button';

const scheduleMenuPositionUpdate = (update: () => void) => {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(update);
    return;
  }
  setTimeout(update, 0);
};

function getCreatePortal():
  | ((ui: React.ReactNode, container: Element) => React.ReactNode)
  | null {
  if (Platform.OS !== 'web') {
    return null;
  }
  try {
    return require('react-dom').createPortal as (
      ui: React.ReactNode,
      container: Element,
    ) => React.ReactNode;
  } catch {
    return null;
  }
}

interface RackTableRowProps {
  rack: RackConfiguration;
  onPress?: (rack: RackConfiguration) => void;
  /** Opens rack editor from overflow menu. Defaults to {@link onPress} when omitted. */
  onEdit?: (rack: RackConfiguration) => void;
  onDuplicate?: (rack: RackConfiguration) => void;
  onDelete?: (rack: RackConfiguration) => void;
  disabled?: boolean;
  /** View-only list: row opens details; edit/duplicate/delete menu hidden. */
  readOnly?: boolean;
  isPlaceholder?: boolean;
  /** Web table card: omit bottom divider on last row. */
  isLastInTable?: boolean;
  testID?: string;
  /** Web drag reorder (#1100) */
  reorderEnabled?: boolean;
  rowIndex?: number;
  isDragging?: boolean;
  isDragOver?: boolean;
  onDragHandleStart?: (rowIndex: number) => void;
  onDragHandleMove?: (pageY: number) => void;
  onDragHandleEnd?: () => void;
  onRowDragOver?: (rowIndex: number) => void;
  onRowDrop?: (rowIndex: number) => void;
  onRowLayout?: (
    rowIndex: number,
    bounds: { top: number; bottom: number },
  ) => void;
  /** Desktop web table vs compact mobile/narrow (#995). */
  tableLayout?: RackTableLayout;
  /** Browse-by-location: omit redundant location column. */
  hideLocationColumn?: boolean;
  /** Devices view: STATUS column with EXISTING/NEW pills. */
  showStatusColumn?: boolean;
  resolveLocationTagName?: (
    locationTagId: number | null | undefined,
  ) => string | null;
}

interface RackRowOverflowMenuProps {
  testID?: string;
  actionsLabel: string;
  menuAvailable: boolean;
  canEdit: boolean;
  openActionLabel: string;
  canDuplicate: boolean;
  canDelete: boolean;
  disabled: boolean;
  useWebDropdown: boolean;
  stopRowActivation?: (event?: { stopPropagation?: () => void }) => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}

const RackRowMenuItem: React.FC<{
  testID?: string;
  onPress: () => void;
  accessibilityLabel: string;
  destructive?: boolean;
  children: React.ReactNode;
}> = ({
  testID,
  onPress,
  accessibilityLabel,
  destructive = false,
  children,
}) => {
  const handlePress = (event?: { stopPropagation?: () => void }) => {
    event?.stopPropagation?.();
    onPress();
  };

  if (Platform.OS === 'web') {
    return (
      <TouchableOpacity
        testID={testID}
        style={styles.menuItem}
        onPress={handlePress}
        activeOpacity={0.7}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
      >
        {children}
      </TouchableOpacity>
    );
  }

  return (
    <Pressable
      testID={testID}
      style={styles.menuItem}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
    >
      {children}
    </Pressable>
  );
};

const WEB_MENU_ITEM_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '8px 12px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: 14,
  fontFamily: 'inherit',
  textAlign: 'left',
};

const RackRowOverflowMenu: React.FC<RackRowOverflowMenuProps> = ({
  testID,
  actionsLabel,
  menuAvailable,
  canEdit,
  openActionLabel,
  canDuplicate,
  canDelete,
  disabled,
  useWebDropdown,
  stopRowActivation,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const hasMenu = menuAvailable;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuAnchorRef = useRef<View>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  // Native: gates the modal dropdown's opacity until measureInWindow resolves,
  // so it never flashes at the wrong position (mirrors FormDropdown).
  const [menuPositioned, setMenuPositioned] = useState(false);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setMenuPositioned(false);
  }, []);

  const updateMenuPosition = useCallback(() => {
    if (!useWebDropdown || typeof window === 'undefined') {
      return;
    }
    const anchor = menuAnchorRef.current;
    if (!anchor?.measureInWindow) {
      return;
    }
    anchor.measureInWindow((x, y, width, height) => {
      setMenuPosition({
        top: y + height + 4,
        left: x + width - RACK_ROW_MENU_MIN_WIDTH,
      });
    });
  }, [useWebDropdown]);

  const runMenuAction = useCallback(
    (action: () => void) => {
      stopRowActivation?.();
      closeMenu();
      action();
    },
    [closeMenu, stopRowActivation],
  );

  const openNativeMenu = useCallback(() => {
    // Open immediately (so it is testable without native measure), then position
    // via measureInWindow; opacity stays 0 until positioned (mirrors FormDropdown).
    setMenuPositioned(false);
    setMenuOpen(true);
    const anchor = menuAnchorRef.current;
    if (!anchor?.measureInWindow) {
      return;
    }
    anchor.measureInWindow((x, y, width, height) => {
      const screen = Dimensions.get('window');
      const itemCount = [canEdit, canDuplicate, canDelete].filter(
        Boolean,
      ).length;
      const dropdownHeight = itemCount * RACK_ROW_MENU_ITEM_HEIGHT + 8;
      const edge = 8;
      let top = y + height + 4;
      let left = x + width - RACK_ROW_MENU_MIN_WIDTH; // right-align to button
      if (top + dropdownHeight > screen.height - edge) {
        top = y - dropdownHeight - 4; // flip above when it would overflow
      }
      if (left + RACK_ROW_MENU_MIN_WIDTH > screen.width - edge) {
        left = screen.width - RACK_ROW_MENU_MIN_WIDTH - edge;
      }
      top = Math.max(edge, top);
      left = Math.max(edge, left);
      setMenuPosition({ top, left });
      setMenuPositioned(true);
    });
  }, [canEdit, canDuplicate, canDelete]);

  const handleMenuPress = (event?: { stopPropagation?: () => void }) => {
    stopRowActivation?.(event);
    if (!hasMenu || disabled) {
      return;
    }
    if (menuOpen) {
      closeMenu();
      return;
    }
    if (useWebDropdown) {
      setMenuOpen(true);
      scheduleMenuPositionUpdate(() => {
        updateMenuPosition();
      });
    } else {
      openNativeMenu();
    }
  };

  useEffect(() => {
    if (!menuOpen || !useWebDropdown) {
      return undefined;
    }
    updateMenuPosition();
    if (
      typeof window === 'undefined' ||
      typeof window.addEventListener !== 'function'
    ) {
      return undefined;
    }
    const handleReposition = () => {
      updateMenuPosition();
    };
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [menuOpen, useWebDropdown, updateMenuPosition]);

  useEffect(() => {
    if (!menuOpen || typeof document === 'undefined') {
      return undefined;
    }
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      const portalSelector = testID
        ? `[data-rack-row-menu-portal="${testID}"]`
        : `[${RACK_ROW_MENU_PORTAL_ATTR}]`;
      const buttonSelector = testID
        ? `[data-rack-row-menu-button="${testID}"]`
        : `[${RACK_ROW_MENU_BUTTON_ATTR}]`;
      const portal = document.querySelector(portalSelector);
      if (portal?.contains(target)) {
        return;
      }
      const buttonEl = document.querySelector(buttonSelector);
      if (buttonEl?.contains(target)) {
        return;
      }
      closeMenu();
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen, closeMenu, testID]);

  const nativeMenuDropdownContent = menuOpen ? (
    <>
      {canEdit ? (
        <RackRowMenuItem
          testID={testID ? `${testID}-menu-edit` : undefined}
          onPress={() => runMenuAction(onEdit)}
          accessibilityLabel={openActionLabel}
        >
          <Edit size={16} color={colors.textPrimary} />
          <Text style={styles.menuItemText}>{openActionLabel}</Text>
        </RackRowMenuItem>
      ) : null}
      {canDuplicate ? (
        <RackRowMenuItem
          testID={testID ? `${testID}-menu-duplicate` : undefined}
          onPress={() => runMenuAction(onDuplicate)}
          accessibilityLabel={RACK_ROW_ACTION_DUPLICATE_LABEL}
        >
          <Copy size={16} color={colors.textPrimary} />
          <Text style={styles.menuItemText}>
            {RACK_ROW_ACTION_DUPLICATE_LABEL}
          </Text>
        </RackRowMenuItem>
      ) : null}
      {canDelete ? (
        <RackRowMenuItem
          testID={testID ? `${testID}-menu-delete` : undefined}
          onPress={() => runMenuAction(onDelete)}
          accessibilityLabel={RACK_ROW_ACTION_DELETE_LABEL}
          destructive
        >
          <Trash2 size={16} color={colors.error} />
          <Text style={[styles.menuItemText, styles.menuItemTextDestructive]}>
            {RACK_ROW_ACTION_DELETE_LABEL}
          </Text>
        </RackRowMenuItem>
      ) : null}
    </>
  ) : null;

  const webPortaledMenu =
    useWebDropdown && menuOpen && typeof document !== 'undefined'
      ? (() => {
          const createPortal = getCreatePortal();
          if (!createPortal) {
            return null;
          }
          return createPortal(
            <div
              data-rack-row-menu-portal={testID ?? 'anonymous'}
              data-testid={testID ? `${testID}-menu-dropdown` : undefined}
              onMouseDown={event => event.stopPropagation()}
              onClick={event => event.stopPropagation()}
              style={{
                position: 'fixed',
                top: menuPosition.top,
                left: menuPosition.left,
                minWidth: RACK_ROW_MENU_MIN_WIDTH,
                zIndex: 999999,
                backgroundColor: colors.backgroundPrimary,
                border: `1px solid ${colors.borderCard}`,
                borderRadius: 8,
                paddingTop: 4,
                paddingBottom: 4,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              {canEdit ? (
                <button
                  type="button"
                  data-testid={testID ? `${testID}-menu-edit` : undefined}
                  style={{
                    ...WEB_MENU_ITEM_STYLE,
                    color: colors.textPrimary,
                  }}
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    runMenuAction(onEdit);
                  }}
                >
                  {openActionLabel}
                </button>
              ) : null}
              {canDuplicate ? (
                <button
                  type="button"
                  data-testid={testID ? `${testID}-menu-duplicate` : undefined}
                  style={{
                    ...WEB_MENU_ITEM_STYLE,
                    color: colors.textPrimary,
                  }}
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    runMenuAction(onDuplicate);
                  }}
                >
                  {RACK_ROW_ACTION_DUPLICATE_LABEL}
                </button>
              ) : null}
              {canDelete ? (
                <button
                  type="button"
                  data-testid={testID ? `${testID}-menu-delete` : undefined}
                  style={{
                    ...WEB_MENU_ITEM_STYLE,
                    color: colors.error,
                  }}
                  onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    runMenuAction(onDelete);
                  }}
                >
                  {RACK_ROW_ACTION_DELETE_LABEL}
                </button>
              ) : null}
            </div>,
            document.body,
          );
        })()
      : null;

  if (!hasMenu) {
    return (
      <View style={styles.menuAnchor}>
        <Pressable
          testID={testID ? `${testID}-menu` : undefined}
          style={[styles.menuButton, styles.menuButtonDisabled]}
          disabled
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityRole="button"
          accessibilityLabel={`More actions for ${actionsLabel}`}
          accessibilityState={{ disabled: true }}
        >
          <MoreHorizontal size={18} color={colors.textMuted} />
        </Pressable>
      </View>
    );
  }

  return (
    <View
      ref={menuAnchorRef}
      style={styles.menuAnchor}
      {...(useWebDropdown && testID
        ? ({ 'data-rack-row-menu-button': testID } as Record<string, string>)
        : {})}
    >
      <Pressable
        testID={testID ? `${testID}-menu` : undefined}
        style={[styles.menuButton, disabled && styles.menuButtonDisabled]}
        onPress={handleMenuPress}
        onPressIn={stopRowActivation}
        disabled={disabled}
        hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        accessibilityRole="button"
        accessibilityLabel={`More actions for ${actionsLabel}`}
        accessibilityHint="Opens rack actions menu"
        accessibilityState={{ expanded: menuOpen, disabled }}
      >
        <MoreHorizontal size={18} color={colors.textSecondary} />
      </Pressable>
      {webPortaledMenu}
      {!webPortaledMenu ? (
        // Native (and web without a DOM portal): render the dropdown in a
        // transparent Modal positioned at the button (mirrors FormDropdown) so it
        // overlays the rows below instead of being painted behind the next row
        // (#995 bug 8). Real web uses the createPortal path above instead.
        <Modal
          visible={menuOpen}
          transparent
          animationType="fade"
          onRequestClose={closeMenu}
          statusBarTranslucent
        >
          <Pressable style={styles.menuModalBackdrop} onPress={closeMenu}>
            <View
              onStartShouldSetResponder={() => true}
              style={[
                styles.menuDropdownModal,
                menuPositioned
                  ? { top: menuPosition.top, left: menuPosition.left }
                  : styles.menuDropdownModalHidden,
              ]}
              testID={testID ? `${testID}-menu-dropdown` : undefined}
            >
              {nativeMenuDropdownContent}
            </View>
          </Pressable>
        </Modal>
      ) : null}
    </View>
  );
};

function rackColumnAlign(column: RackTableColumnKey): 'left' | 'center' {
  return (
    RACK_TABLE_COLUMN_ALIGN[column as keyof typeof RACK_TABLE_COLUMN_ALIGN] ??
    'left'
  );
}

// --- Table row (desktop web + compact mobile/narrow) ---

const RackTableRowTable: React.FC<RackTableRowProps> = ({
  rack,
  onPress,
  onEdit,
  onDuplicate,
  onDelete,
  disabled = false,
  readOnly = false,
  isPlaceholder = false,
  isLastInTable = false,
  testID,
  reorderEnabled = false,
  rowIndex,
  isDragging = false,
  isDragOver = false,
  onDragHandleStart,
  onDragHandleMove,
  onDragHandleEnd,
  onRowDragOver,
  onRowDrop,
  onRowLayout,
  registerRowRemeasure,
  tableLayout = 'desktop',
  hideLocationColumn = false,
  showStatusColumn = false,
  resolveLocationTagName,
}) => {
  const isCompact = tableLayout === 'compact';
  const isNativeCompact = isNativeCompactLayout(tableLayout);
  const columns = rackTableColumnsForLayout(tableLayout, {
    hideLocationColumn,
    showStatusColumn,
  });
  const useWebDropdown = Platform.OS === 'web';
  const isExisting = rack.rackStatus === 'existing';
  const statusLabel = isExisting ? 'Existing' : 'New';
  const isRowNavigable = Boolean(onPress) && !disabled;
  const isInteractive = isRowNavigable;
  const openEditor = onEdit ?? onPress;
  const canEdit =
    Boolean(openEditor) &&
    !disabled &&
    (!readOnly || showStatusColumn) &&
    !isPlaceholder;
  const canDuplicate =
    Boolean(onDuplicate) && !disabled && !readOnly && !isPlaceholder;
  const canDelete =
    Boolean(onDelete) && !disabled && !readOnly && !isPlaceholder;
  const hasMenuActions =
    Boolean(openEditor) || Boolean(onDuplicate) || Boolean(onDelete);
  const { showOverflowMenu, menuDisabled } = repeatableRowMenuState({
    disabled,
    readOnly,
    isPlaceholder,
    canEdit,
    canDuplicate,
    canDelete,
  });

  const openRow = () => {
    if (isInteractive) {
      onPress?.(rack);
    }
  };

  const stopRowActivation = useCallback(
    (event?: { stopPropagation?: () => void }) => {
      event?.stopPropagation?.();
    },
    [],
  );

  const { showDragHandle, dragHandleActive } = repeatableRowDragState({
    reorderEnabled,
    disabled,
    readOnly,
    isPlaceholder,
    rowIndex,
  });
  const dragHandleRef = useRef<View>(null);
  const dropRowRef = useRef<View>(null);

  const { nativeDragHandleProps, onDropRowLayout } = useTableRowDragAndDrop({
    enabled: dragHandleActive,
    rowIndex: rowIndex ?? 0,
    dragHandleRef,
    dropRowRef,
    onDragHandleStart,
    onDragHandleMove,
    onDragHandleEnd,
    onRowDragOver,
    onRowDrop,
    onRowLayout,
    registerRowRemeasure,
  });

  const statusBarCell = (
    <View
      style={[
        styles.statusBar,
        styles.statusBarEmbedded,
        isExisting ? styles.statusBarExisting : styles.statusBarNew,
      ]}
      accessibilityElementsHidden
    />
  );

  const dragHandleCell = showDragHandle ? (
    <View
      ref={dragHandleRef}
      testID={testID ? `${testID}-drag-handle` : undefined}
      style={[
        styles.dragHandleCell,
        !dragHandleActive && styles.dragHandleDisabled,
      ]}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
      pointerEvents="box-only"
      accessibilityRole="button"
      accessibilityLabel="Drag to reorder rack"
      accessibilityState={{ disabled: !dragHandleActive }}
      {...nativeDragHandleProps}
    >
      <GripVertical size={16} color={colors.textMuted} />
    </View>
  ) : null;

  const rowMainCells = (
    <>
      {columns.map(column => {
        const displayValue = isPlaceholder
          ? '—'
          : formatRackTableCell(rack, column);
        const isEmptyCell = displayValue === '—';
        const isEmptyPlaceholder = isPlaceholder || isEmptyCell;
        const roomTagName =
          column === 'ROOM' && !isPlaceholder
            ? resolveLocationTagName?.(rack.locationTagId ?? null)
            : null;
        const subline = !isPlaceholder
          ? getRackColumnSubline(column, rack, tableLayout)
          : undefined;
        const showDoubleLine =
          rackColumnUsesDoubleLine(column, tableLayout) && Boolean(subline);

        const columnAlign = rackColumnAlign(column);
        const isCentered = columnAlign === 'center';

        return (
          <View
            key={column}
            style={[
              isCompact
                ? recordTableStyles.compactTableCell
                : recordTableStyles.tableCell,
              isCentered
                ? recordTableStyles.tableCellCenter
                : recordTableStyles.tableCellStart,
              rackColumnCellStyle(column, tableLayout),
            ]}
          >
            {column === 'STATUS' ? (
              <RepeatableUnitStatusPill
                status={rack.rackStatus}
                testID={testID ? `${testID}-status` : undefined}
              />
            ) : column === 'ROOM' ? (
              <LocationRoomCell
                locationTagName={roomTagName}
                compact={isCompact}
              />
            ) : isNativeCompact && column === 'NAME' ? (
              // Option B (#999): NAME stacks the folded location tag (step view)
              // and/or the STATUS pill (Devices view) beneath the primary text
              // instead of standalone ROOM / STATUS columns. Shared cell keeps
              // all six device tables identical.
              <NativeCompactNameCell
                displayValue={displayValue}
                isEmptyPlaceholder={isEmptyPlaceholder}
                locationTagName={
                  hideLocationColumn
                    ? null
                    : resolveLocationTagName?.(rack.locationTagId ?? null)
                }
                showStatus={showStatusColumn}
                status={rack.rackStatus}
                testID={testID}
              />
            ) : showDoubleLine ? (
              <>
                <Text
                  style={[
                    recordTableStyles.modelPrimaryText,
                    isCompact && recordTableStyles.compactPrimaryText,
                    isEmptyPlaceholder && recordTableStyles.placeholderText,
                  ]}
                  numberOfLines={1}
                >
                  {displayValue}
                </Text>
                <RecordTableCellSubline
                  column={column}
                  subline={subline}
                  textStyle={[
                    recordTableStyles.modelSublineText,
                    isCompact && recordTableStyles.compactSublineText,
                  ]}
                  compact={isCompact}
                />
              </>
            ) : (
              <Text
                style={[
                  recordTableStyles.tableCellText,
                  isCompact && recordTableStyles.compactCellText,
                  isCentered && recordTableStyles.tableCellTextCenter,
                  isEmptyPlaceholder && recordTableStyles.placeholderMuted,
                ]}
                numberOfLines={isCompact && !isCentered ? 2 : 1}
              >
                {displayValue}
              </Text>
            )}
          </View>
        );
      })}
    </>
  );

  // Reserve the trailing actions column so rows line up with the header spacer
  // (#995 bug 7). Exception (#999): on native compact read-only lists (Devices
  // browse) there is never an overflow menu, so collapse it and give the space
  // back to the data columns. Shared helper keeps all six device tables in sync.
  const showCompactActionsColumn = repeatableCompactActionsColumnReserved({
    isCompact,
    readOnly,
    hasOpenAction: Boolean(openEditor) && (!readOnly || showStatusColumn),
  });
  const rowActionsCell = (
    <View
      style={[
        styles.tableActionsCell,
        isCompact &&
          (showCompactActionsColumn
            ? styles.tableActionsCellCompact
            : styles.tableActionsCellHidden),
      ]}
    >
      {showOverflowMenu ? (
        <RackRowOverflowMenu
          testID={testID}
          actionsLabel={
            getRackDisplayName(rack) ?? formatRackTableCell(rack, 'NAME')
          }
          menuAvailable={hasMenuActions}
          canEdit={canEdit}
          openActionLabel={
            readOnly ? RACK_ROW_ACTION_VIEW_LABEL : RACK_ROW_ACTION_EDIT_LABEL
          }
          canDuplicate={canDuplicate}
          canDelete={canDelete}
          disabled={menuDisabled}
          useWebDropdown={useWebDropdown}
          stopRowActivation={stopRowActivation}
          onEdit={() => openEditor?.(rack)}
          onDuplicate={() => onDuplicate?.(rack)}
          onDelete={() => onDelete?.(rack)}
        />
      ) : null}
    </View>
  );

  const a11yLabel = isPlaceholder
    ? 'No rack details yet'
    : `${
        getRackDisplayName(rack) ?? formatRackTableCell(rack, 'NAME')
      }, ${statusLabel} rack`;

  if (!isInteractive) {
    return (
      <View
        ref={dropRowRef}
        onLayout={onDropRowLayout}
        testID={testID || `rack-row-${rack.id ?? 'placeholder'}`}
        style={[
          styles.tableRow,
          styles.tableRowEmbedded,
          isCompact && styles.tableRowCompact,
          isLastInTable && styles.tableRowEmbeddedLast,
          styles.tableRowStatic,
          disabled && styles.tableRowDisabled,
          isDragOver && styles.tableRowDragOver,
        ]}
        accessibilityRole="text"
        accessibilityLabel={a11yLabel}
      >
        {statusBarCell}
        {dragHandleCell}
        {rowMainCells}
        {rowActionsCell}
      </View>
    );
  }

  const rowShellStyle = [
    styles.tableRow,
    styles.tableRowEmbedded,
    isCompact && styles.tableRowCompact,
    isLastInTable && styles.tableRowEmbeddedLast,
    disabled && styles.tableRowDisabled,
    isDragging && styles.tableRowDragging,
    isDragOver && styles.tableRowDragOver,
  ];

  return (
    <View ref={dropRowRef} style={rowShellStyle} onLayout={onDropRowLayout}>
      {statusBarCell}
      {dragHandleCell}
      <Pressable
        testID={testID || `rack-row-${rack.id}`}
        style={({ pressed }) => [
          styles.tableRowMainPressable,
          pressed && styles.tableRowPressed,
        ]}
        onPress={openRow}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={a11yLabel}
        accessibilityHint={
          readOnly ? 'Tap to view rack details' : 'Tap to edit rack'
        }
      >
        {rowMainCells}
      </Pressable>
      {rowActionsCell}
    </View>
  );
};

export const RackTableHeader: React.FC<{
  reorderEnabled?: boolean;
  showDragColumn?: boolean;
  tableLayout?: RackTableLayout;
  hideLocationColumn?: boolean;
  showStatusColumn?: boolean;
  readOnly?: boolean;
}> = ({
  reorderEnabled = false,
  showDragColumn = false,
  tableLayout = 'desktop',
  hideLocationColumn = false,
  showStatusColumn = false,
  readOnly = false,
}) => {
  const columns = rackTableColumnsForLayout(tableLayout, {
    hideLocationColumn,
    showStatusColumn,
  });

  return (
    <RecordTableHeader
      columns={columns}
      getColumnAlign={rackColumnAlign}
      getHeaderLabel={column => rackTableHeaderLabel(column, tableLayout)}
      getColumnCellStyle={(column, layout) =>
        rackColumnCellStyle(column, layout)
      }
      reorderEnabled={reorderEnabled}
      showDragColumn={showDragColumn}
      tableLayout={tableLayout}
      readOnly={readOnly}
      hasOpenAction={readOnly && showStatusColumn}
    />
  );
};

export const RackTableRow: React.FC<RackTableRowProps> = props => (
  <RackTableRowTable {...props} />
);

const styles = StyleSheet.create({
  // Web table
  tableRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderRadius: 8,
    minHeight: 60,
    overflow: 'visible',
    marginBottom: 4,
    cursor: 'pointer' as unknown as undefined,
  },
  tableRowStatic: {
    cursor: 'default' as unknown as undefined,
  },
  tableRowDisabled: {
    opacity: 0.6,
  },
  tableRowDragging: {
    opacity: 0.45,
  },
  tableRowDragOver: {
    backgroundColor: colors.surfaceAccentMuted,
  },
  dragHandleCell: {
    width: 28,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'grab' as unknown as undefined,
  },
  dragHandleDisabled: {
    opacity: 0.4,
    cursor: 'default' as unknown as undefined,
  },
  tableRowPressed: {
    opacity: 0.9,
  },
  statusBar: {
    width: 5,
    flexShrink: 0,
  },
  statusBarEmbedded: {
    borderRadius: 0,
  },
  statusBarExisting: {
    backgroundColor: colors.success,
  },
  statusBarNew: {
    backgroundColor: colors.primary,
  },
  tableActionsCell: {
    width: RECORD_TABLE_ACTIONS_COLUMN_WIDTH,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'visible',
  },
  tableActionsCellCompact: {
    width: 36,
  },
  tableActionsCellHidden: {
    width: 0,
    display: 'none' as unknown as undefined,
  },
  tableRowEmbedded: {
    borderWidth: 0,
    borderRadius: 0,
    marginBottom: 0,
    minHeight: 56,
    backgroundColor: colors.backgroundPrimary,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderCard,
    ...Platform.select({
      web: {
        width: '100%',
        alignSelf: 'stretch',
      },
    }),
  },
  tableRowCompact: {
    minHeight: 52,
  },
  tableRowEmbeddedLast: {
    borderBottomWidth: 0,
  },
  /** Web: row shell is a View; only the main cells are a button (avoids ⋯ menu inside <button>). */
  tableRowMainPressable: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'stretch',
    minWidth: 0,
    cursor: 'pointer' as unknown as undefined,
  },
  // Native card
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderRadius: 10,
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.lg,
    minHeight: 72,
    gap: spacing.md,
    elevation: 1,
  },
  cardStatic: {},
  cardDisabled: {
    opacity: 0.6,
  },
  cardPressed: {
    opacity: 0.9,
  },
  mainColumn: {
    flex: 1,
    minWidth: 0,
    gap: spacing.sm,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    marginTop: 5,
    flexShrink: 0,
  },
  statusDotExisting: {
    backgroundColor: colors.success,
  },
  statusDotNew: {
    backgroundColor: colors.primary,
  },
  titleText: {
    flex: 1,
    fontSize: 16,
    fontWeight: '700',
    color: colors.textPrimary,
    lineHeight: 22,
  },
  titlePlaceholder: {
    fontWeight: '400',
    fontStyle: 'italic',
    color: colors.textMuted,
  },
  chipRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    paddingLeft: 18,
  },
  chip: {
    backgroundColor: colors.backgroundMuted,
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: spacing.sm,
    minWidth: 36,
    alignItems: 'center',
  },
  chipText: {
    fontSize: 12,
    fontWeight: '500',
    color: colors.textSecondary,
  },
  trailingColumn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flexShrink: 0,
  },
  statusPill: {
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: spacing.sm,
  },
  statusPillExisting: {
    backgroundColor: 'rgba(52, 199, 89, 0.15)',
  },
  statusPillNew: {
    backgroundColor: 'rgba(255, 107, 53, 0.15)',
  },
  statusPillText: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  statusPillTextExisting: {
    color: '#1B7A34',
  },
  statusPillTextNew: {
    color: colors.primaryDark,
  },
  menuAnchor: {
    position: 'relative',
    flexShrink: 0,
    zIndex: 20,
  },
  menuButton: {
    width: 32,
    height: 32,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.borderCard,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.backgroundPrimary,
    ...Platform.select({
      web: {
        cursor: 'pointer' as unknown as undefined,
      },
    }),
  },
  /** Portaled web menu — position is on the portal wrapper, not this View. */
  menuDropdownPortal: {
    minWidth: RACK_ROW_MENU_MIN_WIDTH,
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderRadius: 8,
    paddingVertical: spacing.xs,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' as unknown as undefined,
  },
  menuDropdownNative: {
    position: 'absolute',
    top: 36,
    right: 0,
    minWidth: 180,
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderRadius: 8,
    paddingVertical: spacing.xs,
    zIndex: 1000,
    elevation: 8,
  },
  menuModalBackdrop: {
    flex: 1,
  },
  menuDropdownModal: {
    position: 'absolute',
    minWidth: RACK_ROW_MENU_MIN_WIDTH,
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderRadius: 8,
    paddingVertical: spacing.xs,
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.18,
    shadowRadius: 12,
  },
  menuDropdownModalHidden: {
    opacity: 0,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    ...Platform.select({
      web: {
        cursor: 'pointer' as unknown as undefined,
      },
    }),
  },
  menuItemText: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.textPrimary,
  },
  menuItemTextDestructive: {
    color: colors.error,
  },
  menuButtonDisabled: {
    opacity: 0.45,
  },
});
